import { useState, useCallback, useEffect, useRef } from 'react';
import tableService from '../services/tableService';
import { TableColumn } from '../types';
import { TableDataResponse } from '../types/api.types';

interface UseTableDataReturn {
  data: any[];
  columns: TableColumn[];
  loading: boolean;
  error: string | null;
  updateData: (newData: any[]) => void;
  refreshData: (tableId?: string) => Promise<void>;
  validateData: () => { isValid: boolean; errors: string[] };
}

/** 防抖延迟（毫秒） */
const REFRESH_DEBOUNCE_MS = 600;

export const useTableData = (
  initialTableId?: string,
  refreshTrigger?: number,
  collection?: string
): UseTableDataReturn => {
  const [data, setData] = useState<any[]>([]);
  const [columns, setColumns] = useState<TableColumn[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /** 当前 collection ref（随最新传入值同步） */
  const currentCollectionRef = useRef<string | undefined>(collection);
  /** 当前 tableId ref（随最新传入值或 refreshData 调用同步） */
  const currentTableIdRef = useRef<string | undefined>(initialTableId);
  currentCollectionRef.current = collection;
  const requestSeqRef = useRef(0);
  /** 防抖 timer */
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** 标记是否已完成首次加载（避免 initialTableId 和 refreshTrigger 同时触发双请求） */
  const initialLoadDoneRef = useRef(false);
  /** ★ 新增：记录上一次 Effect A 使用的 tableId，避免相同 tableId 重复 fetch */
  const lastFetchedTableIdRef = useRef<string | undefined>(undefined);
  /** ★ 新增：记录上一次处理过的 refreshTrigger 值，避免重复处理 */
  const lastRefreshTriggerRef = useRef<number>(0);

  const updateData = useCallback((newData: any[]) => {
    setData(newData);
    setError(null);
  }, []);

  const _doFetch = useCallback(async (targetTableId: string) => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);

    try {
      const response: TableDataResponse = await tableService.fetchTableData(
        targetTableId,
        currentCollectionRef.current
      );

      // ★ 如果在等待期间又有新请求发出，丢弃本次结果
      if (seq !== requestSeqRef.current) {
        console.log(`[useTableData] 丢弃过期请求 seq=${seq}, current=${requestSeqRef.current}`);
        return;
      }

      setColumns(response.columns);
      setData(response.records);
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      const errorMessage = err instanceof Error ? err.message : '获取表格数据失败';
      setError(errorMessage);
      console.error('刷新表格数据失败:', err);
      setColumns(tableService.generateDefaultColumns());
    } finally {
      if (seq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const refreshData = useCallback(async (tableId?: string) => {
    if (tableId !== undefined) {
      currentTableIdRef.current = tableId;
    }

    const targetTableId = tableId !== undefined ? tableId : currentTableIdRef.current;
    if (!targetTableId) {
      setColumns(tableService.generateDefaultColumns());
      return;
    }

    await _doFetch(targetTableId);
  }, [_doFetch]);

  const validateData = useCallback(() => {
    return tableService.validateTableData(data);
  }, [data]);

  // ─── Effect A：initialTableId 变化时立即请求（首次加载 / 切换表） ───
  useEffect(() => {
    // ★ 只在 tableId 真正变化时才 fetch，避免相同 id 重复请求
    if (initialTableId === lastFetchedTableIdRef.current) {
      return;
    }

    initialLoadDoneRef.current = false;
    lastFetchedTableIdRef.current = initialTableId;
    currentTableIdRef.current = initialTableId;

    refreshData(initialTableId).then(() => {
      initialLoadDoneRef.current = true;
    });
  }, [initialTableId]); // ★ 不依赖 refreshData（引用稳定）

  // ─── Effect B：refreshTrigger 变化时防抖请求 ───
  useEffect(() => {
    if (!refreshTrigger || refreshTrigger <= 0) return;
    // ★ 如果首次加载还没完成，跳过（避免和 Effect A 同时请求）
    if (!initialLoadDoneRef.current) return;
    // ★ 新增：如果这个 trigger 值已经处理过，跳过
    if (refreshTrigger <= lastRefreshTriggerRef.current) return;

    lastRefreshTriggerRef.current = refreshTrigger;

    // ★ 清除之前的防抖 timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      refreshData();
    }, REFRESH_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [refreshTrigger]); // ★ 不依赖 refreshData（引用稳定）

  // ─── 组件卸载时清理 ───
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // 让后续响应全部被丢弃
      requestSeqRef.current = -1;
    };
  }, []);

  return {
    data,
    columns,
    loading,
    error,
    updateData,
    refreshData,
    validateData,
  };
};